import { Box, Button, Stack } from '@mui/material'
import { useCallback, useContext } from 'react'
import AppContext from '../../context/appContext'
import { useFormDefinition } from '../../hooks/data/useFormDefinitions'
import { ReportTemplate } from '../../types/apiModels'
import { useProcessLoading } from '../../utils/Loading/useProcessLoading'
import DynamicForm from '../DynamicForm/DynamicForm'

const getValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.map(item => getValue(item)).join(',')
  }

  if (value && typeof value === 'object' && 'id' in value) {
    return value.id
  }

  return value
}

interface DynamicReportFormProps {
  formDefinitionNamespace: string
  onCancel: () => void
  onSubmit: (data: Record<string, unknown> & { reportId: string }) => Promise<void>
  reportTemplate: ReportTemplate
}

const DynamicReportForm: React.FC<DynamicReportFormProps> = ({
  formDefinitionNamespace,
  onCancel,
  onSubmit,
  reportTemplate,
}) => {
  const { programConfig, currentFarm } = useContext(AppContext)
  const { processLoading } = useProcessLoading()

  const formDefinitionId = programConfig?.formDefinitions?.reports?.[formDefinitionNamespace]?.id
  const { data: formDefinition, isLoading } = useFormDefinition(formDefinitionId)

  const handleSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      await processLoading({
        loadingMessage: 'Generando reporte...',
        successfulMessage: 'Reporte generado exitosamente',
        errorMessage: 'Error al generar el reporte, por favor intente nuevamente',
        doAction: async () => {
          const reportData = {
            ...Object.fromEntries(
              Object.entries(data)
                .map(([key, value]) => {
                  return [key, getValue(value)]
                })
                .filter(([_, value]) => value !== null && value !== undefined && value !== ''),
            ),
            reportId: reportTemplate.id,
          }
          await onSubmit(reportData)
        },
      })
    },
    [onSubmit, processLoading, reportTemplate],
  )

  if (isLoading) {
    return <Box>Cargando formulario...</Box>
  }

  if (!formDefinition) {
    return <Box>Error: No se pudo cargar la definición del formulario</Box>
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <DynamicForm
          data={{ programId: programConfig?.programId, farmId: currentFarm?.id }}
          formDefinition={formDefinition}
          year={new Date().getFullYear()}
          onSubmit={handleSubmit}
        >
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={onCancel}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained">
              Generar Reporte
            </Button>
          </Stack>
        </DynamicForm>
      </Stack>
    </Box>
  )
}

export default DynamicReportForm
